import React, { useEffect, useState, useCallback } from 'react';

import {
  useResourceDefinitions,
  useGetResourceLabel,
  usePermissions,
  useStore,
} from 'react-admin';

import {
  CssBaseline,
  Paper,
  Box,
  styled,
  Theme,
  useMediaQuery,
} from '@mui/material';

import ClockDriftNotification from './components/security/ClockDriftNotification';
import Navbar from './components/layout/navbar/Navbar';
import Sidebar, { drawerWidth, collapsedDrawerWidth } from './components/layout/sidebar/Sidebar';
import Footer from './components/layout/Footer';
import menuGroups from './menuGroups';
import { Actions } from './constants/actions';
import buildMenuItems from './utilities/buildMenuItems';
import {
  PinguDockContextProvider, useDockContext,
} from './components/pingu/PinguDockContext';

const Root = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
});

const AppContentRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'offset' && prop !== 'rightOffset',
})(({ offset, rightOffset = 0 }: { offset: number; rightOffset?: number }) => ({
  display: 'flex',
  minHeight: '100vh',
  flex: '1',
  flexDirection: 'column',
  maxWidth: offset || rightOffset ? `calc(100% - ${offset + rightOffset}px)` : '100%',
  ...(offset ? { marginLeft: offset } : {}),
  ...(rightOffset ? { marginRight: rightOffset } : {}),
}));

const AppContent = ({ offset, children }: { offset: number; children: React.ReactNode }) => {
  const { docked, dockedWidth } = useDockContext();
  return (
    <AppContentRoot offset={offset} rightOffset={docked ? dockedWidth : 0}>
      {children}
    </AppContentRoot>
  );
};

const MainContent = styled(Paper)(({ theme }) => ({
  flex: 1,
  background: theme.palette.background.default,
  '& .MuiPaper-root .MuiPaper-root': {
    boxShadow: 'none',
  },
  padding: 5,
  [theme.breakpoints.up('md')]: {
    padding: 12,
  },
}));

type Variant = 'permanent' | 'temporary'

const Layout = ({ children } : {children?: React.ReactNode }) => {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  // Below the sm breakpoint the toggle opens the drawer as an overlay. On wider
  // screens the drawer is always shown and the toggle switches to the icon strip.
  const variant: Variant = isMobile ? 'temporary' : 'permanent';
  const [open, setOpen] = useState(false);
  // Kept under the preferences. prefix so the choice survives logout
  const [collapsed, setCollapsed] = useStore('preferences.sidebar.collapsed', false);
  useEffect(() => {
    setOpen(false);
  }, [variant]);
  const resourcesDefinitions = useResourceDefinitions<{
    label?: string;
    exact?: boolean;
  }>();
  const getResourceLabel = useGetResourceLabel();
  const { permissions } = usePermissions<Actions[]>();

  const handleDrawerToggle = useCallback(() => {
    if (isMobile) {
      setOpen(!open);
      return;
    }
    setCollapsed(!collapsed);
  }, [isMobile, open, collapsed, setCollapsed]);

  const sidebarCollapsed = !isMobile && collapsed;
  let contentOffset = 0;
  if (!isMobile) {
    contentOffset = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;
  }

  const menuItems = buildMenuItems({
    menuGroups,
    resourceDefinitions: resourcesDefinitions,
    getResourceLabel,
    permissions,
  });

  return (
    <Root>
      <CssBaseline>
        <Sidebar
          variant={variant}
          open={isMobile ? open : true}
          collapsed={sidebarCollapsed}
          onClose={isMobile ? handleDrawerToggle : undefined}
          items={menuItems}
        />
        <PinguDockContextProvider sidebarWidth={contentOffset}>
          <AppContent offset={contentOffset}>
            <Navbar
              onDrawerToggle={handleDrawerToggle}
              collapsible={!isMobile}
              collapsed={sidebarCollapsed}
            />
            <MainContent>
              <ClockDriftNotification />
              {children}
            </MainContent>
            <Footer />
          </AppContent>
        </PinguDockContextProvider>
      </CssBaseline>
    </Root>
  );
};

export default Layout;
